import {
  computed,
  nextTick,
  onMounted,
  ref,
  watch,
  type ModelRef,
  type ShallowReactive,
  type TemplateRef,
} from "vue";
import type { ValidateFunction } from "./validation";
import { useEventListener } from "@vueuse/core";

interface ValidationProps {
  isReadonly?: boolean | undefined;
  isDisabled?: boolean | undefined;
  validate?: ValidateFunction | undefined;
}

type UseValidationArgs = {
  input: TemplateRef<HTMLInputElement | HTMLTextAreaElement>;
  modelValue: ModelRef<any>;
  props: ShallowReactive<ValidationProps>;
};

export default ({ input, modelValue, props }: UseValidationArgs) => {
  const isTouched = ref<boolean>(false);
  const injectedError = ref<string | undefined>();

  const injectError = (error: string) => {
    injectedError.value = error;
  };

  const clearError = () => {
    injectedError.value = undefined;
    isTouched.value = false;
  };

  watch(
    () => modelValue.value,
    () => {
      isTouched.value = true;
      injectedError.value = undefined;
    },
  );

  const validationError = computed<undefined | string>(() => {
    if (typeof props.validate === "function") {
      return props.validate(modelValue.value);
    }
    return undefined;
  });

  const error = computed(() => validationError.value || injectedError.value);

  watch(error, () => {
    input.value!.setCustomValidity(error.value ?? "");
  });

  onMounted(async () => {
    await nextTick(); // https://github.com/nuxt/nuxt/issues/13471#issuecomment-1449898517
    input.value!.setCustomValidity(error.value ?? "");
  });

  useEventListener(input, "focus", () => {
    isTouched.value = true;
  });

  useEventListener(input, "invalid", () => {
    isTouched.value = true;
  });

  return {
    expose: {
      injectError,
      clearError,
    },
    isTouched,
    error,
  };
};
